'use client';

import { useEffect, useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useSelector } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ModeOutlinedIcon from '@mui/icons-material/ModeOutlined';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Typography } from '@mui/material';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Fab from '@mui/material/Fab';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useTheme } from '@mui/material/styles';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import DOMPurify from 'dompurify';
import { useSnackbar } from 'notistack';

import MyConfirmation from '../../../../components/MyConfirmation/MyConfirmation';
import api from '../../../../utils/axiosInstance';

import styles from './Cuestionario.module.css';

export default function Cuestionario(props) {
  const questionRefs = useRef({});
  const currentCompany = useSelector((state) => state.companies.currentCompany);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const scrollPositionRef = useRef(0);

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [file, setFile] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currentQuestionId, setCurrentQuestionId] = useState(null);
  const [checked, setChecked] = useState(() =>
    Object.fromEntries(
      props.questions.map((q) => [q.id, q.conditionalQuestion || false]),
    ),
  );
  const [shouldEdit, setShouldEdit] = useState(false);
  const [editIndex, setEditIndex] = useState(-1);
  const [expandedComposite, setExpandedComposite] = useState({});
  const [activeTab, setActiveTab] = useState({});
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [multiDeleteDialogOpen, setMultiDeleteDialogOpen] = useState(false);

  const saveScrollPosition = () => {
    scrollPositionRef.current =
      window.pageYOffset || document.documentElement.scrollTop;
  };

useEffect(() => {
  if (scrollPositionRef.current > 0) {
    // ✅ Esperar al siguiente frame para que el DOM esté actualizado
    requestAnimationFrame(() => {
      window.scrollTo({
        top: scrollPositionRef.current,
        behavior: 'auto',
      });
    });
  }
}, [props.questions]);

  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    setSelectedQuestions([]);
  };

  const handleSelectQuestion = (questionId) => {
    setSelectedQuestions((prev) => {
      if (prev.includes(questionId)) {
        return prev.filter((id) => id !== questionId);
      }
      return [...prev, questionId];
    });
  };

  const handleSelectAll = () => {
    const rootQuestions = props.questions.filter((q) => !q.parentId);
    if (selectedQuestions.length === rootQuestions.length) {
      setSelectedQuestions([]);
    } else {
      setSelectedQuestions(rootQuestions.map((q) => q.id));
    }
  };

  const handleDeleteSelected = () => {
    if (selectedQuestions.length === 0) return;
    setMultiDeleteDialogOpen(true);
  };

  const onCloseMultiDeleteDialog = (isConfirmed) => {
    if (!isConfirmed) {
      setMultiDeleteDialogOpen(false);
      return;
    }

    saveScrollPosition();
    const count = selectedQuestions.length;

    const remainingQuestions = props.questions.filter(
      (question) => !selectedQuestions.includes(question.id),
    );

    props.setQuestions(remainingQuestions);

    setSelectedQuestions([]);
    setIsSelectionMode(false);
    setMultiDeleteDialogOpen(false);

    enqueueSnackbar(
      `${count} pregunta${count > 1 ? 's' : ''} eliminada${
        count > 1 ? 's' : ''
      }`,
      {
        variant: 'success',
      },
    );
  };

  const organizeQuestionsAutomatically = (questions) => {
    let hasChanges = false;

    const parentQuestions = questions.filter((q) => q.complexParentId);
    const childQuestions = questions.filter((q) => q.complexChildQuestionId);

    if (parentQuestions.length > 0 && childQuestions.length > 0) {
      const organizedQuestions = questions.map((question) => {
        const isParent = parentQuestions.some((p) => p.id === question.id);
        if (isParent && question.parentId) {
          hasChanges = true;
          return { ...question, parentId: null };
        }

        const isChild = childQuestions.some((c) => c.id === question.id);
        if (isChild) {
          const correspondingParent = parentQuestions.find(
            (parent) =>
              parent.complexParentId === question.complexChildQuestionId,
          );

          if (
            correspondingParent &&
            question.parentId !== correspondingParent.id
          ) {
            hasChanges = true;
            return { ...question, parentId: correspondingParent.id };
          }
        }

        return question;
      });

      return { organizedQuestions, hasChanges };
    }

    return { organizedQuestions: questions, hasChanges: false };
  };

  useEffect(() => {
    const hasComplexRelations = props.questions.some(
      (q) => q.complexParentId || q.complexChildQuestionId,
    );

    if (hasComplexRelations) {
      const { organizedQuestions, hasChanges } = organizeQuestionsAutomatically(
        props.questions,
      );

      if (hasChanges) {
        props.setQuestions(organizedQuestions);

        const parentQuestions = organizedQuestions.filter(
          (q) => q.complexParentId,
        );
        parentQuestions.forEach((parentQuestion) => {
          setTimeout(() => {
            setExpandedComposite((prev) => ({
              ...prev,
              [parentQuestion.id]: true,
            }));
            setActiveTab((prev) => ({
              ...prev,
              [parentQuestion.id]: 0,
            }));
          }, 100);
        });
      }
    }
  }, [props.questions.length]);

  const onCloseDeleteDialog = (isConfirmed) => {
    if (!isConfirmed) {
      setDeleteDialogOpen(false);
      return;
    }

    saveScrollPosition();

    const questionToDelete = props.questions.find(
      (q) => q.id === currentQuestionId,
    );

    if (questionToDelete && questionToDelete.questionId) {
      props.setQuestionIdDelete((prev) => ({
        questionIds: [...(prev.questionIds || []), questionToDelete.questionId],
      }));
    }

    props.handleDelete(currentQuestionId);
    setDeleteDialogOpen(false);
  };

  const openDeleteDialog = (id, questionId) => {
    saveScrollPosition();
    setCurrentQuestionId(id);
    setDeleteDialogOpen(true);
  };

  const handleChange = (e, idQuestion) => {
    saveScrollPosition();
    const isChecked = e.target.checked;

    setChecked((prev) => ({
      ...prev,
      [idQuestion]: isChecked,
    }));

    props.setQuestions((prevQuestions) =>
      prevQuestions.map((question) =>
        question.id === idQuestion
          ? { ...question, conditionalQuestion: isChecked }
          : question,
      ),
    );

    props.setConditionalQuestion(isChecked);
    if (isChecked) {
      const index = props.questions.findIndex((q) => q.id === idQuestion);
      setShouldEdit(true);
      setEditIndex(index);
    }
  };

  useEffect(() => {
    if (shouldEdit) {
      props.handleEdit(editIndex);
      setShouldEdit(false);
    }
  }, [props.questions, shouldEdit, editIndex]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    handleSubmit(selectedFile);
  };

  const handleSubmit = async (selectedFile) => {
    const formData = new FormData();
    formData.append('file', selectedFile);

    setIsLoading(true);

    try {
      enqueueSnackbar('Validando archivo...', { variant: 'info' });

      const response = await api.post(`validateExcelQuestionRows/${currentCompany.id}`, formData, {
        responseType: 'blob',
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const contentType = response.headers['content-type'];

      if (contentType.includes('application/json')) {
        const reader = new FileReader();

        reader.onload = (e) => {
          try {
            const json = JSON.parse(e.target.result);
            json.filas.forEach((fila, index) => {
              const data = {
                typeId: Number.parseInt(fila.typeId),
                name: fila.name,
                description: fila.description,
                customOptions: fila.customOptions,
                category: fila.category,
                isRequired: fila.required,
                questionNumber: index + 1,
              };

              if (data.typeId === 1) {
                data.type = 'Texto corto';
                data.voiceEnabled = false;
                data.validationType = 'text';
                data.haveLimit = false;
                data.wordSizeLimit = 500;
              }
              if (data.typeId === 10) {
                data.type = 'E-NPS';
              }
              if (data.typeId === 14) {
                data.type = 'Sentimental';
              }
              if (data.typeId === 15) {
                data.type = 'Relacional';
              }
              if (data.typeId === 21) {
                data.type = 'Texto Informativo';
              }
              if (data.typeId === 23) {
                data.type = 'Fecha';
              }
              if (data.typeId === 29) {
                data.type = 'Tabla';
              }

              if ([2, 16].includes(data.typeId)) {
                data.type = data.typeId === 2 ? 'Escala Likert' : 'Escala Likert-ENG';
                // ✅ Mantener como strings — Likert no usa customOptions, usa options directo
                data.options = props.getOptions(data.typeId); // strings planos
                data.customOptions = data.options; // igual, strings planos
              }

              if (data.typeId === 5) {
                data.type = 'Calificaciones';
                data.stars = Array(3).fill('');
              }

              if ([3, 8].includes(data.typeId)) {
                data.type =
                  data.typeId === 3 ? 'Opción múltiple' : 'Opción única';
                const customOptions = fila.customOptions;
                data.customOptions = customOptions;
                const optionsLength =
                  customOptions.length > 0 ? customOptions.length : 2;
                data.options = Array(optionsLength).fill('');
                if (data.typeId === 8) {
                  data.conditionalQuestion = fila.conditionalQuestion || false;
                  data.childQuestionIds = [];

                  if (
                    fila.childQuestionIds &&
                    Array.isArray(fila.childQuestionIds)
                  ) {
                    data.childQuestionIds = fila.childQuestionIds;
                  }
                }
              }

              if (data.typeId === 15) {
                data.customOptions = Array(2).fill('');
                data.selectOptions = Array(2).fill('');
              }

              if (data.typeId === 19) {
                data.type = 'Bipolar';
                data.textsBipolarBar = {
                  leftText: '',
                  rightText: '',
                  valueRight: '2',
                  valueLeft: '0',
                };
                data.stars = 0;
              }

              if (data.typeId === 22) {
                data.type = 'Escala de Opinión';
                data.textsBipolarBar = {
                  leftText: '',
                  rightText: '',
                  valueRight: '2',
                  valueLeft: '0',
                };
                data.secondSelectOptions = Array.from({ length: 9 }, (_, i) =>
                  (2 + i).toString(),
                );
              }

              if (data.typeId === 24) {
                data.type = 'Suma Constante';
                data.stars = 0;
                data.customOptions = Array(2).fill('');
                data.autoValidate = false;
              }
              if (data.typeId === 25) {
                data.type = 'Matriz';
                data.stars = 0;
                data.customOptions = ['', ''];
                data.autoValidate = false;

                const mo = data.matrixOption || {};

                data.matrixOption = {
                  matrixType:
                    typeof mo.matrixType === 'string'
                      ? mo.matrixType
                      : 'static',
                  options: Array.isArray(mo.options)
                    ? mo.options
                        .map((v) => (v || '').toString().trim())
                        .filter((v) => v !== '')
                    : [],
                  values: Array.isArray(mo.values)
                    ? mo.values
                        .map((v) => (v || '').toString().trim())
                        .filter((v) => v !== '')
                    : [],
                  inputType: 'single',
                };
              }
              if (data.typeId === 26) {
                data.type = 'Compuesta';
                data.stars = 0;
                data.customOptions = Array(2).fill('');
                data.autoValidate = false;
              }

              if (data.typeId === 27) {
                data.type = 'Seleccion Gran escala';
                data.stars = 0;
                data.customOptions = Array(2).fill('');
                data.autoValidate = false;
              }

              if (data.typeId === 28) {
                data.type = 'Multimedia';
                data.stars = 0;
                data.customOptions = Array(2).fill('');
                data.autoValidate = false;
              }
              if (data.typeId === 30) {
                data.type = 'Archivos';
                data.stars = 1;
                data.limitOptions = 1;
                // Normalizar customOptions a strings limpios
                data.customOptions = (fila.customOptions || [])
                  .map(opt => typeof opt === 'string' ? opt : opt?.value)
                  .filter(v => v && v.trim() !== '');
                
                // Si viene vacío, poner default
                if (data.customOptions.length === 0) {
                  data.customOptions = ['pdf'];
                }
              }
              props.handleAddQuestion(
                data,
                data.typeId,
                true,
                Number(data.category.split('-')[0]),
              );
            });

            enqueueSnackbar('Archivo válido.', { variant: 'success' });
          } catch (err) {
            enqueueSnackbar('Error al procesar el JSON recibido.', {
              variant: 'error',
            });
          }
        };

        reader.readAsText(response.data);
      } else if (
        contentType.includes(
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        )
      ) {
        const blob = new Blob([response.data], { type: contentType });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = 'Errores_Preguntas.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        enqueueSnackbar('Se detectaron errores. Archivo descargado.', {
          variant: 'warning',
        });
      } else {
        enqueueSnackbar('Respuesta inesperada del servidor.', {
          variant: 'error',
        });
      }
    } catch (error) {
      const contentType = error.response?.headers['content-type'];

      if (
        error.response?.status === 400 &&
        contentType?.includes(
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        )
      ) {
        const blob = new Blob([error.response.data], { type: contentType });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = 'Errores_Preguntas.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        enqueueSnackbar('Se detectaron errores. Archivo descargado.', {
          variant: 'warning',
        });
      } else {
        enqueueSnackbar('Ocurrió un error inesperado al validar el archivo.', {
          variant: 'error',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const organizeQuestions = (questions) => {
    const questionMap = new Map();
    const rootQuestions = [];

    questions.forEach((q, index) => {
      questionMap.set(q.id, { ...q, children: [] });
    });

    questions.forEach((q) => {
      const question = questionMap.get(q.id);
      if (q.parentId && questionMap.has(q.parentId)) {
        const parent = questionMap.get(q.parentId);
        parent.children.push(question);
      } else {
        rootQuestions.push(question);
      }
    });

    return rootQuestions;
  };

  const handleAddChildQuestion = (parentId) => {
    const currentChildren = props.questions.filter(
      (q) => q.parentId === parentId,
    );
    if (currentChildren.length >= 5) {
      enqueueSnackbar(
        'Máximo 5 preguntas hijas permitidas por pregunta compuesta',
        { variant: 'warning' },
      );
      return;
    }

    const newQuestion = {
      id: `child_${Date.now()}_${Math.random()}`,
      name: 'Nueva pregunta hija',
      description: 'Descripción de la pregunta hija',
      typeId: 8,
      parentId: parentId,
      order: 0,
      options: Array(2).fill(''),
    };

    props.setQuestions((prevQuestions) => [...prevQuestions, newQuestion]);
    enqueueSnackbar('Pregunta hija agregada', { variant: 'success' });
  };

  const handleMoveToComposite = (questionId, targetParentId) => {
    saveScrollPosition();
    const questionToMove = props.questions.find((q) => q.id === questionId);

    if (
      questionToMove &&
      questionToMove.typeId !== 8 &&
      questionToMove.typeId !== 1 &&
      questionToMove.typeId !== 22 &&
      questionToMove.typeId !== 2 &&
      questionToMove.typeId !== 16 &&
      questionToMove.typeId !== 14 &&
      questionToMove.typeId !== 3 &&
      questionToMove.typeId !== 30
    ) {
      enqueueSnackbar(
        'Solo preguntas tipo 1, 2, 16, 14, 3, 8 o 22 pueden ser hijas de preguntas compuestas',
        { variant: 'warning' }
      );
      return;
    }

    const currentChildren = props.questions.filter(
      (q) => q.parentId === targetParentId,
    );
    if (currentChildren.length >= 10) {
      enqueueSnackbar(
        'Máximo 10 preguntas hijas permitidas por pregunta compuesta',
        { variant: 'warning' },
      );
      return;
    }

    props.setQuestions((prevQuestions) =>
      prevQuestions.map((question) => {
        if (question.id === questionId) {
          return {
            ...question,
            parentId: targetParentId,
            customOptions: question.customOptions?.filter(
              (opt) => !opt.isOther,
            ),
          };
        }
        return question;
      }),
    );

    enqueueSnackbar('Pregunta movida al grupo compuesto', {
      variant: 'success',
    });

    setActiveTab((prev) => ({
      ...prev,
      [targetParentId]: 0,
    }));

    setExpandedComposite((prev) => ({
      ...prev,
      [targetParentId]: true,
    }));
  };

  const handleRemoveFromComposite = (questionId) => {
    saveScrollPosition();
    props.setQuestions((prevQuestions) =>
      prevQuestions.map((question) =>
        question.id === questionId ? { ...question, parentId: null } : question,
      ),
    );
    enqueueSnackbar('Pregunta removida del grupo compuesto', {
      variant: 'success',
    });

    setTimeout(() => {
      setExpandedComposite((prev) => ({ ...prev }));
    }, 100);
  };

  const handleDownload = async () => {
    setIsLoadingTemplate(true);
    try {
      const res = await api.get(`plantillas/excel/${currentCompany?.id}`, {
        responseType: 'blob',
      });
      const contentType =
        res.headers['content-type'] ||
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([res.data], { type: contentType });

      const cd = res.headers['content-disposition'];
      const match = cd && /filename\*?=(?:UTF-8''|")?([^\";]+)/i.exec(cd);
      const filename = match ? decodeURIComponent(match[1]) : 'plantilla.xlsx';

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename.replace(/["']/g, '').trim();
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setIsLoadingTemplate(false);
    }
  };

  const scrollToQuestion = (questionId) => {
    const element = questionRefs.current[questionId];
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });

      // Efecto visual de highlight
      element.style.transition = 'background-color 0.3s';
      element.style.backgroundColor = '#ffebee';

      setTimeout(() => {
        element.style.backgroundColor = 'transparent';
      }, 2000);
    }
  };

  useEffect(() => {
    if (props.scrollToQuestionId) {
      scrollToQuestion(props.scrollToQuestionId);
      if (props.onScrollComplete) {
        props.onScrollComplete();
      }
    }
  }, [props.scrollToQuestionId]);

  const stripHtml = (html) => (html || '').replace(/<[^>]*>/g, '').trim();
  const renderQuestion = (question, index, level = 0) => {
    const isComposite = question.typeId === 26;
    const isExpanded = expandedComposite[question.id] || false;
    const hasChildren = question.children && question.children.length > 0;
    const realIndex = props.questions.findIndex((q) => q.id === question.id);
    const childrenCount = props.questions.filter(
      (q) => q.parentId === question.id,
    ).length;
    const isSelected = selectedQuestions.includes(question.id);

    return (
      <div
        key={question.id}
        style={{ marginLeft: level * 20 }}
        ref={(el) => (questionRefs.current[question.id] = el)}
      >
        <div
          className={styles.drag}
          style={{
            backgroundColor: isSelected
              ? 'rgba(25, 118, 210, 0.08)'
              : 'transparent',
            borderRadius: '8px',
            transition: 'background-color 0.2s',
            position: 'relative',
          }}
        >
          <div className={styles.showedit}>
            {isSelectionMode && level === 0 && (
              <Checkbox
                checked={isSelected}
                onChange={() => handleSelectQuestion(question.id)}
                sx={{ mr: 1 }}
              />
            )}

            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 1.2,
                py: 0.4,
                backgroundColor: '#eeeeee',
                borderRadius: '16px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                mb: 0.5,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: '#333',
                  whiteSpace: 'nowrap',
                }}
              >
                {question.type}
              </Typography>
            </Box>
            <div>
              <div className={styles.number}>
                <span className={styles.order}>
                  {level > 0 && (
                    <SubdirectoryArrowRightIcon
                      sx={{ fontSize: 16, mr: 0.5 }}
                    />
                  )}
                  Q{index + 1}
                  {level > 0 && (
                    <Chip
                      label="Hija"
                      size="small"
                      color="secondary"
                      sx={{ ml: 1, fontSize: '10px', height: 16 }}
                    />
                  )}
                </span>
              </div>

              <div className={styles.question_data}>
                <div className={styles.quest}>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(question.name),
                    }}
                  />

                  {isComposite && (
                    <Badge
                      badgeContent={childrenCount}
                      color="primary"
                      sx={{
                        '& .MuiBadge-badge': {
                          right: -3,
                          top: 13,
                          border: '2px solid white',
                          padding: '0 4px',
                          fontSize: '0.75rem',
                          minWidth: '20px',
                          height: 20,
                        },
                      }}
                    >
                      <Chip
                        label="Compuesta"
                        size="small"
                        color="primary"
                        sx={{ ml: 1 }}
                      />
                    </Badge>
                  )}
                  {isComposite && childrenCount > 0 && (
                    <Typography
                      variant="caption"
                      sx={{
                        ml: 1,
                        px: 1,
                        py: 0.5,
                        backgroundColor: 'primary.light',
                        color: 'white',
                        borderRadius: 1,
                        fontSize: '0.7rem',
                        fontWeight: 'bold',
                      }}
                    >
                      {childrenCount}/10 hijas
                    </Typography>
                  )}
                </div>
                <div
                  className={styles.desc}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(question.description),
                  }}
                />

                {!isSelectionMode && (
                  <div className={styles.editdelete}>
                    <IconButton
                      onClick={() => {
                        saveScrollPosition();
                        props.handleEdit(realIndex);
                      }}
                    >
                      <ModeOutlinedIcon sx={{ fontSize: '30px' }} />
                    </IconButton>

                    <IconButton
                      onClick={() =>
                        openDeleteDialog(question.id, question.questionId)
                      }
                    >
                      <DeleteForeverOutlinedIcon sx={{ fontSize: '30px' }} />
                    </IconButton>

                    {level === 0 &&
                      (question.typeId === 8 ||
                        question.typeId === 1 ||
                        question.typeId === 22 || 
                        question.typeId === 2 ||
                        question.typeId === 16 ||
                        question.typeId === 14 ||
                        question.typeId === 3 ||
                        question.typeId === 30) &&
                      organizeQuestions(props.questions).some(
                        (q) => q.typeId === 26 && q.id !== question.id,
                      ) && (
                        <Select
                          size="small"
                          displayEmpty
                          value={question.parentId || ''}
                          onChange={(e) => {
                            const parentId = e.target.value;
                            if (parentId) {
                              handleMoveToComposite(question.id, parentId);
                            }
                          }}
                          sx={{ minWidth: 200, marginRight: '10px' }}
                        >
                          <MenuItem value="">
                            <em>Mover a grupo compuesto</em>
                          </MenuItem>
                          {organizeQuestions(props.questions)
                            .filter(
                              (q) => q.typeId === 26 && q.id !== question.id,
                            )
                            .map((q) => {
                              const qChildrenCount = props.questions.filter(
                                (child) => child.parentId === q.id,
                              ).length;
                              return (
                                <MenuItem
                                  key={q.id}
                                  value={q.id}
                                  disabled={qChildrenCount >= 10}
                                >
                                  {(q.name || '').replace(/<[^>]*>/g, '').trim()} ({qChildrenCount}/10)
                                </MenuItem>
                              );
                            })}
                        </Select>
                      )}

                    {level > 0 && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        onClick={() => handleRemoveFromComposite(question.id)}
                      >
                        Remover del grupo
                      </Button>
                    )}

                    {question.typeId === 8 &&
                      !question.parentId &&
                      (() => {
                        const rootQuestions = props.questions.filter(
                          (q) => !q.parentId,
                        );
                        const currentQuestionIndex = rootQuestions.findIndex(
                          (q) => q.id === question.id,
                        );
                        const isLastRootQuestion =
                          currentQuestionIndex === rootQuestions.length - 1;
                        return !isLastRootQuestion;
                      })() && (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={checked[question.id] || false}
                              onChange={(e) => handleChange(e, question.id)}
                            />
                          }
                          label="¿Es condicional?"
                        />
                      )}

                    {isComposite && hasChildren && (
                      <IconButton
                        onClick={() =>
                          setExpandedComposite((prev) => ({
                            ...prev,
                            [question.id]: !isExpanded,
                          }))
                        }
                      >
                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {isComposite && hasChildren && isExpanded && (
          <Box
            sx={{
              ml: 4,
              mt: 2,
              mb: 2,
              border: '1px solid #e0e0e0',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ mb: 2, color: 'primary.main' }}
            >
              Preguntas hijas de "{stripHtml(question.name)}" ({childrenCount}/10)
            </Typography>

            <Tabs
              value={activeTab[question.id] || 0}
              onChange={(_, newValue) =>
                setActiveTab((prev) => ({
                  ...prev,
                  [question.id]: newValue,
                }))
              }
              variant="scrollable"
              scrollButtons="auto"
            >
              {question.children.map((child, childIndex) => (
                <Tab
                  key={child.id}
                  label={`Hija ${childIndex + 1}`}
                  sx={{ minWidth: 100 }}
                />
              ))}
            </Tabs>
            {question.children.map((child, childIndex) => (
              <div
                key={child.id}
                style={{
                  marginTop: 16,
                  display:
                    (activeTab[question.id] || 0) === childIndex
                      ? 'block'
                      : 'none',
                }}
              >
                {(activeTab[question.id] || 0) === childIndex && (
                  <div>{renderQuestion(child, childIndex, level + 1)}</div>
                )}
              </div>
            ))}
          </Box>
        )}

        <Divider variant="middle" sx={{ my: 1 }} />
      </div>
    );
  };
  useEffect(() => {
    const organizedQuestions = organizeQuestions(props.questions);
    organizedQuestions.forEach((question) => {
      if (
        question.typeId === 26 &&
        question.children &&
        question.children.length > 0
      ) {
        if (activeTab[question.id] === undefined) {
          setActiveTab((prev) => ({
            ...prev,
            [question.id]: 0,
          }));
        }
      }
    });
  }, [props.questions]);
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.ctrlKey && e.key === 'd') {
        e.preventDefault();
        toggleSelectionMode();
      }
      if (
        e.key === 'Delete' &&
        isSelectionMode &&
        selectedQuestions.length > 0
      ) {
        handleDeleteSelected();
      }

      if (e.ctrlKey && e.key === 'a' && isSelectionMode) {
        e.preventDefault();
        handleSelectAll();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isSelectionMode, selectedQuestions]);

  const handleChildDragEnd = (result, parentId) => {
    if (!result.destination) return;

    const { source, destination } = result;
    if (source.index === destination.index) return;

    saveScrollPosition();

    props.setQuestions((prevQuestions) => {
      // Separar hijas de este padre y el resto
      const children = prevQuestions.filter((q) => q.parentId === parentId);
      const others = prevQuestions.filter((q) => q.parentId !== parentId);

      // Reordenar hijas
      const reordered = [...children];
      const [moved] = reordered.splice(source.index, 1);
      reordered.splice(destination.index, 0, moved);

      // Reconstruir el array manteniendo la posición original del bloque
      // Insertar las hijas reordenadas en el lugar donde estaban
      const result = [];
      let childInserted = false;
      for (const q of others) {
        result.push(q);
        // Insertar hijas justo después del padre
        if (q.id === parentId && !childInserted) {
          result.push(...reordered);
          childInserted = true;
        }
      }
      // Si el padre no estaba en others (no debería pasar), insertar al final
      if (!childInserted) result.push(...reordered);

      return result;
    });

    // Mantener el tab activo apuntando al elemento movido
    setActiveTab((prev) => ({
      ...prev,
      [parentId]: destination.index,
    }));
  };
  return (
    <div className={styles.title}>
      <div className={styles.questions}>
        <div className={styles.top}>
          <div className={styles.left}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Cuestionario de encuesta - Con preguntas compuestas
            </Typography>
            <p
              style={{
                fontSize: '14px',
                fontWeight: '300',
                lineHeight: '20px',
                letterSpacing: '0.25px',
              }}
            >
              Puede reorganizar, editar o eliminar preguntas. Las preguntas
              compuestas pueden tener preguntas hijas organizadas en tabs.
            </p>
          </div>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Button
              variant={isSelectionMode ? 'contained' : 'outlined'}
              onClick={toggleSelectionMode}
              color={isSelectionMode ? 'error' : 'primary'}
              sx={{ height: 48, whiteSpace: 'nowrap' }}
            >
              {isSelectionMode ? 'Cancelar selección' : 'Seleccionar múltiples'}
            </Button>

            {isSelectionMode && (
              <>
                <Button
                  variant="text"
                  onClick={handleSelectAll}
                  size="small"
                  sx={{ alignSelf: 'center' }}
                >
                  {selectedQuestions.length ===
                  props.questions.filter((q) => !q.parentId).length
                    ? 'Deseleccionar todo'
                    : 'Seleccionar todo'}
                </Button>

                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDeleteSelected}
                  disabled={selectedQuestions.length === 0}
                  startIcon={<DeleteIcon />}
                  sx={{ height: 48 }}
                >
                  Eliminar ({selectedQuestions.length})
                </Button>
              </>
            )}

            {!isSelectionMode && (
              <>
                <Button
                  color="primary"
                  variant="outlined"
                  onClick={props.handleAdd}
                  startIcon={<AddIcon />}
                  sx={{ height: 48, whiteSpace: 'nowrap' }}
                >
                  Pregunta
                </Button>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadFileIcon />}
                    disabled={isLoading}
                    sx={{ height: 48, whiteSpace: 'nowrap' }}
                  >
                    {isLoading
                      ? 'Validando archivo...'
                      : 'Importar preguntas (.xlsx)'}
                    <input
                      type="file"
                      hidden
                      accept=".xlsx"
                      onChange={handleFileChange}
                    />
                  </Button>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    disabled={isLoadingTemplate}
                    onClick={handleDownload}
                    sx={{ height: 48, whiteSpace: 'nowrap' }}
                  >
                    {!isLoadingTemplate
                      ? 'Descargar plantilla xlsx'
                      : 'Descargando Archivo...'}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </div>
        {props.questions.length === 0 ? (
          <p
            style={{
              fontSize: '12px',
              fontWeight: '300',
              lineHeight: '16px',
              letterSpacing: '0.25px',
            }}
          >
            ¡Aún no se agregaron preguntas!
          </p>
        ) : (
          <div className={styles.data}>
            <DragDropContext onDragEnd={props.onEnd}>
              <Droppable droppableId="droppable">
                {(provided, snapshot) => {
                  return (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {organizeQuestions(props.questions).map(
                        (question, index) => {
                          return (
                            <Draggable
                              key={question.id}
                              draggableId={question.id}
                              index={index}
                              isDragDisabled={isSelectionMode}
                            >
                              {(provided, snapshot) => {
                                return (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                  >
                                    {renderQuestion(question, index)}
                                  </div>
                                );
                              }}
                            </Draggable>
                          );
                        },
                      )}
                      {provided.placeholder}
                    </div>
                  );
                }}
              </Droppable>
            </DragDropContext>
          </div>
        )}
      </div>

      {!isSelectionMode && (
        <Tooltip title="Agregar pregunta" placement="right">
          <Fab
            color="primary"
            aria-label="agregar pregunta"
            onClick={props.handleAdd}
            disabled={isLoading}
            sx={{
              position: 'fixed',
              bottom: isMobile ? 16 : 24,
              left: isMobile ? 80 : 200,
              zIndex: 1200,
              boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
              '&:hover': {
                boxShadow: '0 6px 12px rgba(0,0,0,0.3)',
              },
            }}
          >
            <AddIcon />
          </Fab>
        </Tooltip>
      )}

      <MyConfirmation
        open={deleteDialogOpen}
        onClose={onCloseDeleteDialog}
        title="¿Estás seguro de eliminar esta pregunta?"
        message="Esta acción no se puede deshacer."
      />

      <MyConfirmation
        open={multiDeleteDialogOpen}
        onClose={onCloseMultiDeleteDialog}
        title={`¿Estás seguro de eliminar ${selectedQuestions.length} pregunta${
          selectedQuestions.length > 1 ? 's' : ''
        }?`}
        message="Esta acción no se puede deshacer."
      />
    </div>
  );
}
